import React, { useState } from 'react';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import * as yup from 'yup';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import { useUpdateAlpacaFeeMutation } from '../../redux/features/alpacaFee/alpacaFee.apiSlice';
import { AlpacaFee, AlpacaFeeType, UpdateAlpacaFeeRequest } from '../../types/alpacaFee';

/** What one unit of each fee's value is charged on. */
export const FEE_VALUE_UNIT: Record<AlpacaFeeType, string> = {
  [AlpacaFeeType.REG]: 'per USD sold',
  [AlpacaFeeType.TAF]: 'per share sold',
  [AlpacaFeeType.CAT]: 'per share bought or sold',
  [AlpacaFeeType.WIRE_OUT_INTERNATIONAL]: 'USD per wire',
};

type FormValues = {
  alpacaFeeValue: string;
  platformFeeValue: string;
  alpacaMaxFeePerTrade: string;
  platformMaxFeePerTrade: string;
  feeReference: string;
};

const toText = (value: number | string | null) => (value === null || value === undefined ? '' : String(value));

const toFormValues = (fee: AlpacaFee | null): FormValues => ({
  alpacaFeeValue: toText(fee?.alpacaFeeValue ?? null),
  platformFeeValue: toText(fee?.platformFeeValue ?? null),
  alpacaMaxFeePerTrade: toText(fee?.alpacaMaxFeePerTrade ?? null),
  platformMaxFeePerTrade: toText(fee?.platformMaxFeePerTrade ?? null),
  feeReference: fee?.feeReference || '',
});

/** Blank → null (clears it); otherwise a number. */
const toNumberOrNull = (text: string) => (text.trim() === '' ? null : Number(text));

const toRequest = (id: number, values: FormValues): UpdateAlpacaFeeRequest => ({
  id,
  alpacaFeeValue: Number(values.alpacaFeeValue),
  platformFeeValue: toNumberOrNull(values.platformFeeValue),
  alpacaMaxFeePerTrade: toNumberOrNull(values.alpacaMaxFeePerTrade),
  platformMaxFeePerTrade: toNumberOrNull(values.platformMaxFeePerTrade),
  feeReference: values.feeReference.trim() || null,
});

const optionalNumber = (rule: (schema: yup.NumberSchema) => yup.NumberSchema) =>
  yup
    .string()
    .test('number', 'Must be a number', (value) => !value || !Number.isNaN(Number(value)))
    .test('rule', 'Not allowed', (value) => !value || rule(yup.number()).isValidSync(Number(value)));

const schema = yup.object({
  alpacaFeeValue: yup
    .string()
    .required("Alpaca's value is required")
    .test('number', 'Must be a number, 0 or more', (value) => value !== undefined && Number(value) >= 0),
  platformFeeValue: optionalNumber((n) => n.min(0)).label('Our value'),
  alpacaMaxFeePerTrade: optionalNumber((n) => n.positive()).label("Alpaca's cap"),
  platformMaxFeePerTrade: optionalNumber((n) => n.positive()).label('Our cap'),
  feeReference: yup.string().max(255, 'At most 255 characters'),
});

/** The server's refusals, mapped to the field they belong to or to a message for the whole form. */
const describeSaveError = (error: any): { field?: keyof FormValues; message: string } => {
  switch (error?.data?.message) {
    case 'ALPACA_FEE_PLATFORM_VALUE_BELOW_ALPACA_VALUE':
      return {
        field: 'platformFeeValue',
        message: "While 'Use our value' is on, our value must be set and at least Alpaca's.",
      };
    case 'ALPACA_FEE_NOT_FOUND':
      return { message: 'This fee no longer exists.' };
    default:
      return { message: error?.data?.message || 'The fee could not be saved.' };
  }
};

interface AlpacaFeeDialogProps {
  open: boolean;
  fee: AlpacaFee | null;
  onClose: () => void;
}

/** Edit a fee's values. Only new charges use them — fees already taken keep the values they were worked out with. */
export function AlpacaFeeDialog({ open, fee, onClose }: AlpacaFeeDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [updateFee, { isLoading }] = useUpdateAlpacaFeeMutation();
  const [saveError, setSaveError] = useState('');

  const { values, touched, errors, handleChange, handleBlur, setFieldError, submitForm, resetForm } =
    useFormik<FormValues>({
      initialValues: toFormValues(fee),
      enableReinitialize: true,
      validationSchema: schema,
      onSubmit: async (formValues) => {
        if (!fee) return;

        setSaveError('');

        try {
          await updateFee(toRequest(fee.id, formValues)).unwrap();
          enqueueSnackbar('Fee saved.', { variant: 'success' });
          resetForm();
          onClose();
        } catch (err) {
          const { field, message } = describeSaveError(err);

          if (field) {
            setFieldError(field, message);
          } else {
            setSaveError(message);
          }
        }
      },
    });

  const handleClose = () => {
    setSaveError('');
    resetForm();
    onClose();
  };

  const fieldProps = (name: keyof FormValues) => ({
    id: `alpaca-fee-${name}`,
    name,
    value: values[name],
    onChange: handleChange,
    onBlur: handleBlur,
    error: touched[name] && Boolean(errors[name]),
    margin: 'normal' as const,
    fullWidth: true,
    inputProps: { dir: 'ltr', inputMode: 'decimal' as const },
  });
  const unit = fee ? FEE_VALUE_UNIT[fee.feeType] : '';

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{fee ? `Edit ${fee.feeType}` : 'Edit fee'}</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mt: 1 }}>
          Applies to new charges only. Fees already taken keep the values they were worked out with.
        </Alert>
        <Grid container columnSpacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              {...fieldProps('alpacaFeeValue')}
              required
              label="Alpaca value"
              helperText={(touched.alpacaFeeValue && errors.alpacaFeeValue) || unit}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              {...fieldProps('platformFeeValue')}
              label="Our value"
              helperText={(touched.platformFeeValue && errors.platformFeeValue) || `${unit}. Blank = none.`}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              {...fieldProps('alpacaMaxFeePerTrade')}
              label="Alpaca cap per trade (USD)"
              helperText={(touched.alpacaMaxFeePerTrade && errors.alpacaMaxFeePerTrade) || 'Blank = no cap.'}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              {...fieldProps('platformMaxFeePerTrade')}
              label="Our cap per trade (USD)"
              helperText={(touched.platformMaxFeePerTrade && errors.platformMaxFeePerTrade) || "Blank = Alpaca's cap."}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              {...fieldProps('feeReference')}
              label="Reference"
              placeholder="SR-FINRA-2026-021"
              helperText={(touched.feeReference && errors.feeReference) || 'Where the value comes from.'}
              inputProps={{ dir: 'ltr', maxLength: 255 }}
            />
          </Grid>
        </Grid>

        {saveError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {saveError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <LoadingButton
          type="button"
          color="primary"
          variant="contained"
          endIcon={<SaveIcon />}
          loading={isLoading}
          loadingPosition="end"
          onClick={submitForm}
        >
          Save
        </LoadingButton>
        <Button color="inherit" variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
